import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import TrueImpactModal from 'shared/design-system/components/TrueImpactModal';
import { TrueImpactFloatMessage } from 'shared/design-system/components/TrueImpactFloatLoading';
import TrueImpactLoading from 'shared/components/TrueImpactLoading';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { TrueImpactButtonWithLoading } from 'shared/design-system/components/TrueImpactButton';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { AnswerInput, useProgram } from 'shared/providers/ProgramProvider';
import {
  isAnswerFilled,
  SERVICE_TYPE_QUESTION_ID,
} from 'shared/providers/ProgramProvider/utils';
import { ResizableSplitView } from 'npo/pages/BetaFeedback/ResizableSplitView';
import ProgramSidebar from '../ProgramQuestions/ProgramSidebar';
import ProgramQuestionPane, {
  inputFromAnswer,
  emptyInput,
} from '../ProgramQuestions/ProgramQuestionPane';
import ReviewScreen from '../ProgramQuestions/ReviewScreen';
import SidebarIdentity from '../ProgramQuestions/SidebarIdentity';
import { getReportStage, ReportsTable } from '../ReportGroup';
import SnapshotDebug from './SnapshotDebug';
import PublishedScreen from './PublishedScreen';
import OverwriteProgramAnswerModal from '../ProgramQuestions/OverwriteProgramAnswerModal';
import {
  faChartLineUp,
  faSparkles,
  faArrowRight,
} from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { isPrefilledFromProgram } from '../ProgramQuestions/prefill';

// Float the close/expand controls over the top-right of the content so
// the sidebar runs edge-to-edge to the top of the modal.
const addStyles = `
  .modal-wrapper { position: relative; }
  .modal-wrapper .modal-header { position: absolute; top: 0; right: 0; z-index: 20; }
  .modal-wrapper .close { padding: ${SPACERS.tiny} ${SPACERS.small}; }
`;

const chipIn = keyframes`
  from { opacity: 0; transform: translateX(8px); }
  to { opacity: 1; transform: translateX(0); }
`;

const ringPulse = keyframes`
  0% { transform: scale(1); opacity: 0.5; }
  100% { transform: scale(1.12, 1.4); opacity: 0; }
`;

const breathe = keyframes`
  0%, 100% { box-shadow: 0 0 0 3px rgba(33, 150, 83, 0.18); }
  50% { box-shadow: 0 0 0 5px rgba(33, 150, 83, 0.32); }
`;

const Body = styled.div`
  display: flex;
  height: 100%;
  min-height: 60vh;
`;

const RightPaneWrapper = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const RightPaneContent = styled.div`
  flex: 1;
  overflow: auto;
  min-height: 0;
`;

const RightPaneFooter = styled.div`
  padding: ${SPACERS.small} ${SPACERS.large};
  background-color: ${COLORS.whiteBase};
  border-top: 1px solid ${COLORS.grayLight};
  .error-text {
    text-align: center;
    margin-bottom: ${SPACERS.tiny};
  }
  /* Previous anchors left, the primary action anchors right, both aligned
     to the same 900px column the question content uses. */
  .buttons-wrapper {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
    max-width: 900px;
    margin: 0 auto;
    .next-button {
      button {
        min-width: 200px;
        font-size: 1rem;
        padding: ${SPACERS.small} ${SPACERS.large};
      }
    }
    .next-group {
      display: flex;
      align-items: center;
      gap: ${SPACERS.small};
    }
    .next-button.has-prefill {
      isolation: isolate;
      button {
        position: relative;
        z-index: 1;
        animation: ${breathe} 3.5s ease-in-out infinite;
        transition: box-shadow 0.3s ease;
      }
      &::before {
        content: '';
        position: absolute;
        inset: 0;
        border-radius: 6px;
        border: 2px solid ${COLORS.success};
        pointer-events: none;
        z-index: 0;
        opacity: 0;
        animation: ${ringPulse} 1.6s ease-out 0.4s 2;
      }
      &:hover,
      &:focus-within {
        button {
          animation: none;
          box-shadow: 0 0 0 3px ${COLORS.successBackground};
        }
        &::before {
          animation: none;
          opacity: 0;
        }
      }
    }
    .prefilled-hint {
      display: inline-flex;
      align-items: center;
      gap: ${SPACERS.tiny};
      padding: 6px 12px 6px 10px;
      border-radius: 999px;
      border: 1px solid ${COLORS.successSubdued}99;
      color: ${COLORS.success};
      font-size: 0.75rem;
      font-weight: 600;
      letter-spacing: 0.01em;
      white-space: nowrap;
      margin: 0;
      animation: ${chipIn} 0.3s ease-out both;
      .dot {
        width: 7px;
        height: 7px;
        border-radius: 50%;
        background: ${COLORS.success};
      }
      svg {
        width: 11px;
        height: 11px;
      }
      .arrow {
        margin-left: 2px;
      }
    }

    @media (prefers-reduced-motion: reduce) {
      .next-button.has-prefill button,
      .next-button.has-prefill::before,
      .prefilled-hint {
        animation: none;
      }
      .next-button.has-prefill::before {
        opacity: 0;
      }
      .next-button.has-prefill button {
        box-shadow: 0 0 0 3px ${COLORS.successBackground};
      }
    }
  }
`;

type Screen = 'question' | 'review';

interface SnapshotBodyProps {
  onClose: () => void;
  report: ReportsTable;
}

/**
 * Snapshot editor body — near-identical clone of `ProgramQuestionsBody`
 * from `ReportsBeta/ProgramQuestions/ProgramQuestionsModal.tsx`, wired to
 * the shared ProgramContext supplied by ReportSnapshotProvider. The two
 * bodies diverge only in copy (report vs program) and the submit label.
 * TODO: extract a single shared body once feature parity is proven.
 */
const SnapshotBody: React.FC<SnapshotBodyProps> = ({ onClose, report }) => {
  const {
    questions,
    isAllComplete,
    submitProgram,
    submitting,
    activeQuestionId,
    setActiveQuestionId,
    findQuestionById,
    saveAnswer,
    saving,
    getProgramProfileAnswer,
    saveProgramAnswer,
    savingProgramAnswer,
  } = useProgram();

  const [submitError, setSubmitError] = useState<string | null>(null);
  const [screen, setScreen] = useState<Screen>('question');
  // When a save produces a report-level answer that differs from the
  // shared program-profile answer, we pause the advance step and ask
  // the user whether to overwrite the program answer. `pendingOverwrite`
  // stores everything needed to (a) render the comparison modal and
  // (b) resume the advance once the user picks skip / overwrite.
  const [pendingOverwrite, setPendingOverwrite] = useState<{
    questionId: number;
    questionTitle: React.ReactNode;
    programAnswerText: string;
    reportAnswerText: string;
    advance: () => void;
    input: AnswerInput;
  } | null>(null);
  // Seed `input` from the report's own answer when present; otherwise,
  // for questions flagged `isProgramQuestion`, fall back to the
  // program-profile answer so the field auto-populates with the
  // program's value. The input remains editable and whatever the user
  // types will be persisted as the report's own answer on save.
  const resolveSeedAnswer = (qid: number | null) => {
    if (qid == null) return null;
    const found = findQuestionById(qid);
    const q = found?.question;
    if (!q) return null;
    if (q.answer) return q.answer;
    if (q.isProgramQuestion && getProgramProfileAnswer) {
      return getProgramProfileAnswer(q.questionId) ?? null;
    }
    return null;
  };

  const [input, setInput] = useState<AnswerInput>(() =>
    inputFromAnswer(resolveSeedAnswer(activeQuestionId)),
  );
  const [localError, setLocalError] = useState<string | null>(null);

  useEffect(() => {
    if (!localError) return;
    const timer = window.setTimeout(() => setLocalError(null), 4000);
    return () => window.clearTimeout(timer);
  }, [localError]);

  const [hideFooter, setHideFooter] = useState(false);
  const [nextBlocked, setNextBlocked] = useState(false);

  const seedAnswer = resolveSeedAnswer(activeQuestionId);

  // Sync `input` synchronously (in render) when the active question
  // changes so QuestionRenderer — which uses `key={question.questionId}`
  // and mounts on question switch — sees `value` for the NEW question on
  // the very first render, not the previous question's answer. Doing
  // this in a useEffect leaks stale `input` into the mounting child; the
  // legacy `initialValue`-only budget fields then lock in that stale
  // value. Mirrors the pattern in ProgramQuestionsModal.
  const lastQuestionIdRef = useRef(activeQuestionId);
  if (lastQuestionIdRef.current !== activeQuestionId) {
    lastQuestionIdRef.current = activeQuestionId;
    setInput(inputFromAnswer(seedAnswer));
    setLocalError(null);
  }

  // Re-sync when the cached answer for the CURRENT question changes
  // (e.g. after a save patches the cache). Same-question re-syncs are
  // safe as an effect because the QuestionRenderer key doesn't change,
  // so no child remount race.
  useEffect(() => {
    setInput(inputFromAnswer(seedAnswer));
  }, [seedAnswer]);

  useEffect(() => {
    if (screen === 'review' && activeQuestionId != null) {
      setScreen('question');
    }
  }, [activeQuestionId]);

  const isPublished =
    report.status === 'Published' || submitting.type === 'REQUEST_SUCCESS';

  const isSubmitting = submitting.type === 'REQUEST_START';
  const isSaving = saving.type === 'REQUEST_START';

  const errorText = useMemo(() => {
    if (submitError) return submitError;
    if (questions.type === 'REQUEST_ERROR') return questions.error?.message;
    return undefined;
  }, [submitError, questions]);

  const flatQuestionIds = useMemo(() => {
    if (questions.type !== 'REQUEST_SUCCESS') return [] as number[];
    return questions.data.sections.flatMap(s =>
      s.questions.map(q => q.questionId),
    );
  }, [questions]);

  const currentIndex = useMemo(
    () =>
      activeQuestionId == null ? -1 : flatQuestionIds.indexOf(activeQuestionId),
    [flatQuestionIds, activeQuestionId],
  );

  const prevQuestionId =
    currentIndex > 0 ? flatQuestionIds[currentIndex - 1] : null;
  const nextQuestionId =
    currentIndex >= 0 && currentIndex < flatQuestionIds.length - 1
      ? flatQuestionIds[currentIndex + 1]
      : null;
  const lastQuestionId =
    flatQuestionIds.length > 0
      ? flatQuestionIds[flatQuestionIds.length - 1]
      : null;

  const handleSubmit = async () => {
    setSubmitError(null);
    const res = await submitProgram();
    if (!res.success) setSubmitError(res.error);
  };

  /**
   * Switch to a different question. We reset `input` synchronously here
   * rather than relying on the [activeQuestionId, foundAnswer] effect —
   * some field components (e.g. LocationField) seed their own useState
   * from `value.text` on mount, so if the parent's `input` still holds
   * the previous question's text when they first render, that stale
   * value leaks in as their initial state. Resetting before the render
   * that switches the question guarantees the child field mounts with
   * the correct starting value.
   */
  const goToQuestion = (targetQuestionId: number) => {
    const targetAnswer = resolveSeedAnswer(targetQuestionId);
    setInput(targetAnswer ? inputFromAnswer(targetAnswer) : emptyInput());
    setLocalError(null);
    setActiveQuestionId(targetQuestionId);
  };

  const handleSaveAndAdvance = async () => {
    setLocalError(null);
    const found = findQuestionById(activeQuestionId);
    if (!found) return;
    const { question } = found;

    const isEmpty = !isAnswerFilled(input);
    const hasExistingAnswer = Boolean(question.answer);

    if (isEmpty && !hasExistingAnswer && !question.required) {
      if (nextQuestionId != null) {
        goToQuestion(nextQuestionId);
      } else {
        setScreen('review');
      }
      return;
    }

    const res = await saveAnswer(question, input);
    if (!res.success) {
      setLocalError(res.error);
      return;
    }

    const serverNextId =
      question.questionId === SERVICE_TYPE_QUESTION_ID
        ? res.nextUnansweredQuestionId
        : null;
    const targetId = serverNextId || nextQuestionId;

    const advance = () => {
      if (targetId != null) {
        goToQuestion(targetId);
      } else {
        setScreen('review');
      }
    };

    // Program-level question with a diverging program answer? Ask the
    // user before pushing the new value up. Otherwise, advance normally.
    if (question.isProgramQuestion && getProgramProfileAnswer) {
      const programAnswer = getProgramProfileAnswer(question.questionId);
      const programText = (programAnswer?.text ?? '').trim();
      const reportText = (input.text ?? '').trim();
      if (programText && reportText && programText !== reportText) {
        setPendingOverwrite({
          questionId: question.questionId,
          questionTitle:
            question.textNode ?? question.text ?? question.name ?? null,
          programAnswerText: programText,
          reportAnswerText: reportText,
          input,
          advance,
        });
        return;
      }
    }

    advance();
  };

  const closePendingOverwrite = () => setPendingOverwrite(null);

  const handleSkipOverwrite = () => {
    const p = pendingOverwrite;
    if (!p) return;
    closePendingOverwrite();
    p.advance();
  };

  const handleConfirmOverwrite = async () => {
    const p = pendingOverwrite;
    if (!p || !saveProgramAnswer) return;
    const res = await saveProgramAnswer(p.questionId, p.input);
    if (!res.success) {
      // Keep the modal open; error is rendered via `savingProgramAnswer`.
      return;
    }
    closePendingOverwrite();
    p.advance();
  };

  const handlePrevious = () => {
    if (screen === 'review') {
      setScreen('question');
      if (activeQuestionId == null && lastQuestionId != null) {
        goToQuestion(lastQuestionId);
      }
      return;
    }
    if (prevQuestionId != null) {
      goToQuestion(prevQuestionId);
    }
  };

  const handleEditFromReview = (questionId: number) => {
    goToQuestion(questionId);
    setScreen('question');
  };

  const loading = questions.type === 'REQUEST_START';

  const previousDisabled =
    isSubmitting ||
    isSaving ||
    (screen === 'review'
      ? flatQuestionIds.length === 0
      : prevQuestionId == null);

  const nextIsSubmit = screen === 'review';
  const nextText = nextIsSubmit
    ? 'Submit Impact Snapshot'
    : nextQuestionId != null
    ? 'Save & Continue'
    : 'Save & Review';
  const nextDisabled = nextIsSubmit
    ? isSubmitting || loading
    : isSaving || loading || activeQuestionId == null || nextBlocked;
  const nextIsLoading = nextIsSubmit ? isSubmitting : isSaving;
  const nextOnClick = nextIsSubmit ? handleSubmit : handleSaveAndAdvance;
  const activeQuestion = findQuestionById(activeQuestionId)?.question;
  const showPrefilledHint =
    screen === 'question' &&
    !!activeQuestion &&
    isPrefilledFromProgram(activeQuestion, getProgramProfileAnswer);

  return (
    <>
      <SnapshotDebug
        report={report}
        questions={questions}
        isAllComplete={isAllComplete}
        activeQuestionId={activeQuestionId}
        activeQuestion={findQuestionById(activeQuestionId)}
      />
      <OverwriteProgramAnswerModal
        open={pendingOverwrite != null}
        questionTitle={pendingOverwrite?.questionTitle ?? null}
        programAnswerText={pendingOverwrite?.programAnswerText ?? ''}
        reportAnswerText={pendingOverwrite?.reportAnswerText ?? ''}
        savingState={savingProgramAnswer}
        onSkip={handleSkipOverwrite}
        onOverwrite={handleConfirmOverwrite}
      />
      <TrueImpactModal
        open={true}
        onClose={onClose}
        showExpandButton={true}
        initialExpandValue={true}
        maxWidth="1200px"
        hideFooter={true}
        addStyles={addStyles}
      >
        {loading ? (
          <TrueImpactLoading />
        ) : isPublished ? (
          <PublishedScreen
            onClose={onClose}
            reportId={report.dbId}
            programName={report.program?.name || report.name}
            previousStatus={report.status}
            previousStage={report.stage}
          />
        ) : (
          <Body>
            <ResizableSplitView
              defaultWidth={25}
              minWidth={15}
              maxWidth={50}
              left={
                <ProgramSidebar
                  theme="snapshot"
                  backgroundColor="ctaPrimaryBackgroundLightest"
                  onOpenReview={() => setScreen('review')}
                  isReviewActive={screen === 'review'}
                  onSelectQuestion={handleEditFromReview}
                  identity={
                    <SidebarIdentity
                      theme="snapshot"
                      kickerIcon={faChartLineUp}
                      breadcrumb={report.program?.name ?? undefined}
                      title={report.name || 'Impact Snapshot'}
                      subtitle={
                        report.stage ? (
                          <>
                            Impact stage:{' '}
                            <strong>{getReportStage(report.stage)}</strong>
                          </>
                        ) : (
                          'Snapshot report shared with your donor.'
                        )
                      }
                      progress={
                        questions.type === 'REQUEST_SUCCESS'
                          ? {
                              answered: questions.data.answeredCount,
                              total: questions.data.totalQuestions,
                            }
                          : undefined
                      }
                      badge={
                        report.status === 'Draft'
                          ? { tone: 'neutral', label: 'Draft' }
                          : report.status === 'Finalized'
                          ? { tone: 'success', label: 'Finalized' }
                          : { tone: 'info', label: report.status }
                      }
                    />
                  }
                />
              }
              right={
                <RightPaneWrapper>
                  <RightPaneContent>
                    {screen === 'question' ? (
                      <ProgramQuestionPane
                        input={input}
                        onInputChange={setInput}
                        localError={localError}
                        disabled={isSaving}
                        report={report}
                        onHideFooterChange={setHideFooter}
                        onBlockedChange={setNextBlocked}
                        onPrefilledCtaClick={handleSaveAndAdvance}
                        prefilledCtaLabel={nextText}
                        prefilledCtaDisabled={nextDisabled}
                      />
                    ) : (
                      <ReviewScreen onEditQuestion={handleEditFromReview} />
                    )}
                  </RightPaneContent>
                  {!(hideFooter && screen === 'question') && (
                    <RightPaneFooter>
                      {errorText && (
                        <TrueImpactText color="critical" className="error-text">
                          {errorText}
                        </TrueImpactText>
                      )}
                      <div className="buttons-wrapper">
                        <TrueImpactButtonWithLoading
                          variant="secondary-large"
                          disabled={previousDisabled}
                          isLoading={false}
                          onClick={handlePrevious}
                          className="previous-button"
                        >
                          Previous
                        </TrueImpactButtonWithLoading>
                        <div className="next-group">
                          {showPrefilledHint && (
                            <TrueImpactText
                              variant="body-small"
                              className="prefilled-hint"
                            >
                              <span className="dot" aria-hidden />
                              <FontAwesomeIcon icon={faSparkles} />
                              Prefilled &middot; save to keep
                              <FontAwesomeIcon
                                icon={faArrowRight}
                                className="arrow"
                              />
                            </TrueImpactText>
                          )}
                          <TrueImpactButtonWithLoading
                            disabled={nextDisabled}
                            isLoading={nextIsLoading}
                            onClick={nextOnClick}
                            className={
                              showPrefilledHint
                                ? 'next-button has-prefill'
                                : 'next-button'
                            }
                          >
                            {nextText}
                          </TrueImpactButtonWithLoading>
                        </div>
                      </div>
                    </RightPaneFooter>
                  )}
                </RightPaneWrapper>
              }
            />
          </Body>
        )}
        <TrueImpactFloatMessage type="error" message={localError} />
      </TrueImpactModal>
    </>
  );
};

export default SnapshotBody;
